import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import type {
  BuyTokensInput,
  BuyTokensOutput,
  CheckoutInput,
  CheckoutOutput,
  PortalInput,
  PortalOutput,
  SubscriptionOutput,
  TokenUsageOutput,
} from '@shared/validators/billing.schema';

/*
 * Billing-data-kerros (Sessio 8b).
 *
 * TanStack Query -hookit Stripe-billingin nakymalle:
 *
 *  - useSubscriptionQuery -> GET /api/billing/subscription. Nykyinen plan, status,
 *    trial-loppu, grace-period, stripe-customer-flag.
 *  - useTokenUsageQuery -> GET /api/billing/token-usage. Kuluvan kauden token-kulutus
 *    + endpoint-kohtainen jaottelu.
 *  - useCheckoutMutation -> POST /api/billing/checkout. Onnistuessa redirect Stripe
 *    Checkout -sivulle (window.location.href).
 *  - usePortalMutation -> POST /api/billing/portal. Onnistuessa redirect Customer
 *    Portaliin.
 *  - useInvalidateBilling -> apuhookki: invalidoi subscription + token-usage query:t
 *    samalla kertaa (esim. kun palataan onnistuneesta checkoutista ?checkout=success).
 *
 * Refresh-flow ja 401-kasittely tulevat apiFetch:lta + AuthContextilta.
 */

const QUERY_KEYS = {
  subscription: ['billing', 'subscription'] as const,
  tokenUsage: ['billing', 'token-usage'] as const,
};

/** Subscription pidetaan tuoreena 30 s -- BillingPage:n kayttoon. */
const SUBSCRIPTION_STALE_MS = 30 * 1000;

/** Token-kayttoa katsotaan harvemmin -- 60 s riittaa. */
const TOKEN_USAGE_STALE_MS = 60 * 1000;

export function useSubscriptionQuery() {
  return useQuery<SubscriptionOutput>({
    queryKey: QUERY_KEYS.subscription,
    queryFn: ({ signal }) =>
      apiFetch<SubscriptionOutput>('/api/billing/subscription', { signal }),
    staleTime: SUBSCRIPTION_STALE_MS,
  });
}

export function useTokenUsageQuery() {
  return useQuery<TokenUsageOutput>({
    queryKey: QUERY_KEYS.tokenUsage,
    queryFn: ({ signal }) =>
      apiFetch<TokenUsageOutput>('/api/billing/token-usage', { signal }),
    staleTime: TOKEN_USAGE_STALE_MS,
  });
}

/**
 * Plan-valinta-mutaatio. Backend palauttaa diskriminoidun tuloksen:
 *  - mode='checkout' (uusi tilaus): selain ohjataan Stripe Checkout -sivulle.
 *  - mode='updated' (olemassa olevan tilauksen plan-vaihto, proration): EI redirectia
 *    -> invalidoidaan subscription-query (webhook paivittaa planin). Komponentti
 *    nayttaa onnistumis-toastin.
 * Tama estaa duplikaattitilaukset: aktiivisen tilaajan plan-vaihto muokkaa
 * olemassa olevaa tilausta uuden Checkoutin sijaan.
 */
export function useCheckoutMutation() {
  const queryClient = useQueryClient();
  return useMutation<CheckoutOutput, Error, CheckoutInput>({
    mutationFn: (input) =>
      apiFetch<CheckoutOutput>('/api/billing/checkout', { method: 'POST', body: input }),
    onSuccess: (data) => {
      if (data.mode === 'checkout' && data.url) {
        window.location.href = data.url;
      } else {
        void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subscription });
      }
    },
  });
}

/**
 * Token-pack-osto (Sessio 19b). Onnistuessa selain ohjataan Stripe Checkoutiin
 * (mode=payment). Webhook (checkout.session.completed) kirjaa allowance-rivin;
 * BillingPage invalidoi query:t ?checkout=success-paluulla.
 */
export function useBuyTokensMutation() {
  return useMutation<BuyTokensOutput, Error, BuyTokensInput>({
    mutationFn: (input) =>
      apiFetch<BuyTokensOutput>('/api/billing/buy-tokens', { method: 'POST', body: input }),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });
}

/**
 * Customer Portal -mutaatio. Onnistuessa selain ohjataan Stripe Customer Portaliin
 * (peruutus, korttitietojen vaihto, laskut). Palaaminen: return_url = /billing.
 */
export function usePortalMutation() {
  return useMutation<PortalOutput, Error, PortalInput>({
    mutationFn: (input) =>
      apiFetch<PortalOutput>('/api/billing/portal', { method: 'POST', body: input }),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });
}

/**
 * Invalidoi billing-tilan query:t. Kaytetaan kun palataan checkout-onnistumisesta
 * tai portal-cancel:sta (URL-parametri ?checkout=success), jotta plan-muutos
 * naytetaan kayttajalle ilman manuaalista reloadia.
 */
export function useInvalidateBilling() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subscription });
    void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tokenUsage });
  };
}
